// frontend/src/components/clienthealth/AssignPlanModal.tsx
//
// One-click "Assign Plan" modal for the Client Health screen.
// Modeled on CreateSubscriptionModal in SubscriptionsPage.tsx, but with the
// client (WON lead) pre-selected. On submit it POSTs /api/subscriptions
// { leadId, planId, startDate } — the backend auto-creates the plan's
// deliverables as part of that call, so we do NOT also hit
// /stage-gates/auto-create-deliverables (that would duplicate them).
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, PackageCheck } from 'lucide-react';
import type { SubscriptionPlan } from '../../types/subscription';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface AssignPlanModalProps {
  isOpen: boolean;
  leadId: string;
  company: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssignPlanModal: React.FC<AssignPlanModalProps> = ({
  isOpen,
  leadId,
  company,
  onClose,
  onSuccess,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: plans = [], isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ['subscription-plans', 'active'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      const result = await response.json();
      return (result.data || []).filter((p: SubscriptionPlan) => p.isActive);
    },
    enabled: isOpen,
  });

  // Reset form whenever the modal (re)opens for a client
  useEffect(() => {
    if (isOpen) {
      setSelectedPlanId('');
      setStartDate(new Date().toISOString().split('T')[0]);
      setSubmitting(false);
      setError(null);
    }
  }, [isOpen, leadId]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedPlanId) {
      setError('Please select a plan.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      // Create the subscription. Backend auto-creates the plan's deliverables.
      const response = await fetch(`${API_URL}/subscriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          leadId,
          planId: selectedPlanId,
          startDate: new Date(startDate).toISOString(),
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to assign plan.');
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to assign plan. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPlanLabel = (plan: SubscriptionPlan) => {
    const rupees = (plan.price / 100).toLocaleString('en-IN');
    const cadence =
      plan.billingCycle === 'MONTHLY' ? 'mo' : plan.billingCycle === 'QUARTERLY' ? 'qtr' : 'yr';
    return `${plan.name} — ₹${rupees}/${cadence}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <PackageCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assign Plan</h2>
            <p className="text-sm text-gray-500 truncate max-w-[18rem]">{company || 'Selected client'}</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Plan selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Plan</label>
            {plansLoading ? (
              <div className="text-sm text-gray-500">Loading plans…</div>
            ) : plans.length === 0 ? (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                No active plans found. Create one in{' '}
                <span className="font-medium">Subscriptions → Manage Plans</span> first.
              </div>
            ) : (
              <select
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {formatPlanLabel(plan)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Start date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* What happens */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-700 mb-1">What happens</p>
            <p className="text-sm text-blue-800">
              The client is put on this plan and its deliverables are created automatically.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedPlanId || submitting || plans.length === 0}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            {submitting ? 'Assigning…' : 'Assign Plan'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPlanModal;
